import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  RefreshCw,
  Twitter,
  Facebook,
  Instagram,
  MessageCircle,
  Heart,
  Share,
  ExternalLink,
  Filter,
  CheckCircle,
  Globe
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface CuratedPost {
  id: string;
  platform: string;
  author: string;
  content: string;
  timestamp: string;
  engagement: {
    likes: number;
    comments: number;
    shares: number;
  };
  source: string;
  media_url?: string;
  post_url?: string;
}

const CuratedFeedPage: React.FC = () => {
  const { user } = useAuth();
  const { accounts: connectedAccounts } = useConnectedAccounts();
  const [posts, setPosts] = useState<CuratedPost[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchCuratedContent = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      console.log('🎯 Fetching curated content...');

      // Fetch content from all connected platforms
      const feedPromises: Promise<CuratedPost[]>[] = [];

      if (Array.isArray(connectedAccounts)) {
        connectedAccounts.forEach(account => {
          if (!account.is_active) return;

          switch (account.platform) {
            case 'twitter':
              feedPromises.push(fetchTwitterCurated());
              break;
            case 'facebook':
              feedPromises.push(fetchFacebookCurated());
              break;
            case 'instagram':
              feedPromises.push(fetchInstagramCurated());
              break;
          }
        });
      }

      const results = await Promise.allSettled(feedPromises);
      const allPosts: CuratedPost[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          allPosts.push(...result.value);
        } else {
          console.error(`❌ Platform feed ${index} failed:`, result.reason);
        }
      });

      // Apply content filtering to get only approved content
      const curatedPosts = await applyCurationFilters(allPosts);
      
      // Sort by timestamp (newest first)
      const sortedPosts = curatedPosts.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setPosts(sortedPosts);
      setLastRefresh(new Date());
      
      toast.success(`Loaded ${sortedPosts.length} curated posts from ${connectedAccounts.filter(acc => acc.is_active).length} platforms`);
      
    } catch (error) {
      console.error('❌ Failed to fetch curated content:', error);
      toast.error('Failed to load curated content');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTwitterCurated = async (): Promise<CuratedPost[]> => {
    try {
      const { data, error } = await supabase.functions.invoke('fetch-twitter-feed', {
        body: { user_id: user?.id }
      });

      if (error || !data?.success) return [];

      return (data.tweets || []).map((tweet: any) => ({
        id: `twitter-${tweet.id}`,
        platform: 'twitter',
        author: tweet.author?.name || tweet.author?.username || 'Twitter User',
        content: tweet.text || '',
        timestamp: tweet.created_at || new Date().toISOString(),
        engagement: {
          likes: tweet.public_metrics?.like_count || 0,
          comments: tweet.public_metrics?.reply_count || 0,
          shares: tweet.public_metrics?.retweet_count || 0
        },
        source: 'twitter_api',
        post_url: `https://twitter.com/${tweet.author?.username}/status/${tweet.id}`
      }));
    } catch (error) {
      console.error('❌ Twitter curated fetch error:', error);
      return [];
    }
  };

  const fetchFacebookCurated = async (): Promise<CuratedPost[]> => {
    try {
      const { data, error } = await supabase.functions.invoke('fetch-facebook-feed', {
        body: { user_id: user?.id }
      });

      if (error || !data?.success) return [];

      return (data.posts || []).map((post: any) => ({
        id: `facebook-${post.id}`,
        platform: 'facebook',
        author: post.from?.name || 'Facebook User',
        content: post.message || post.story || '',
        timestamp: post.created_time || new Date().toISOString(),
        engagement: {
          likes: post.likes?.summary?.total_count || 0,
          comments: post.comments?.summary?.total_count || 0,
          shares: post.shares?.count || 0
        },
        source: 'facebook_api',
        post_url: post.permalink_url
      }));
    } catch (error) {
      console.error('❌ Facebook curated fetch error:', error);
      return [];
    }
  };

  const fetchInstagramCurated = async (): Promise<CuratedPost[]> => {
    try {
      const { data, error } = await supabase.functions.invoke('fetch-instagram-feed', {
        body: { user_id: user?.id }
      });

      if (error || !data?.success) return [];

      return (data.posts || []).map((post: any) => ({
        id: `instagram-${post.id}`,
        platform: 'instagram',
        author: post.username || 'Instagram User',
        content: post.caption || '',
        timestamp: post.timestamp || new Date().toISOString(),
        engagement: {
          likes: post.like_count || 0,
          comments: post.comments_count || 0,
          shares: 0
        },
        source: 'instagram_api',
        media_url: post.media_url,
        post_url: post.permalink
      }));
    } catch (error) {
      console.error('❌ Instagram curated fetch error:', error);
      return [];
    }
  };

  const applyCurationFilters = async (content: CuratedPost[]): Promise<CuratedPost[]> => {
    if (!user || content.length === 0) return content;

    try {
      console.log(`🧹 Applying curation filters to ${content.length} items...`);
      
      const { data, error } = await supabase.functions.invoke('apply-content-filters', {
        body: {
          user_id: user.id,
          content_items: content.map(item => ({
            id: item.id,
            platform: item.platform,
            content: item.content,
            author: item.author,
            timestamp: item.timestamp,
            metadata: { source: item.source, engagement: item.engagement }
          }))
        }
      });

      if (error) {
        console.error('❌ Content filtering error:', error);
        return content; // Return all content if filtering fails
      }

      if (data?.success && data?.filtered_content) {
        console.log(`✅ Curation complete: ${data.stats?.allowed || 0} approved posts`);
        
        // Return only the approved content
        return data.filtered_content.map((item: any) => {
          const originalItem = content.find(orig => orig.id === item.id);
          return originalItem || item;
        });
      }

      return content;
    } catch (error) {
      console.error('❌ Curation filtering error:', error);
      return content;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'twitter':
        return <Twitter className="h-4 w-4 text-blue-500" />;
      case 'facebook':
        return <Facebook className="h-4 w-4 text-blue-600" />;
      case 'instagram':
        return <Instagram className="h-4 w-4 text-pink-500" />;
      default:
        return <Globe className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatEngagement = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  useEffect(() => {
    fetchCuratedContent();
  }, [user, connectedAccounts]);

  const connectedPlatforms = connectedAccounts?.filter(acc => acc.is_active) || [];

  return (
    <div className="w-full max-w-none space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              Curated Feed
            </h1>
            <p className="text-muted-foreground mt-2">
              Your approved content from all connected platforms, filtered through your digital fence
            </p>
          </div>
          <Button 
            onClick={fetchCuratedContent} 
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Connected Platforms Status */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Monitored Platforms
              </h3>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {connectedPlatforms.length} Active
              </Badge>
            </div>
            <div className="flex flex-wrap gap-3">
              {connectedPlatforms.map((account) => (
                <div key={account.id} className="flex items-center gap-2 bg-accent/50 rounded-lg px-3 py-2">
                  {getPlatformIcon(account.platform)}
                  <span className="text-sm font-medium">{account.account_username}</span>
                  <CheckCircle className="h-3 w-3 text-green-500" />
                </div>
              ))}
              {connectedPlatforms.length === 0 && (
                <p className="text-muted-foreground text-sm">No platforms connected</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Bar */}
      {lastRefresh && (
        <div className="mb-6 flex items-center justify-between text-sm text-muted-foreground bg-accent/30 rounded-lg px-4 py-3">
          <div className="flex items-center gap-4">
            <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
            <span>•</span>
            <span>{posts.length} curated posts</span>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            Filtered Content
          </Badge>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-20 w-full mb-4" />
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Curated Posts */}
      {!isLoading && posts.length === 0 && (
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <Filter className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Curated Content Available</h3>
              <p className="text-muted-foreground mb-4">
                No content has passed through your filters yet, or no platforms are connected.
              </p>
              <Button onClick={fetchCuratedContent} variant="outline">
                Refresh Content
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      {!isLoading && posts.length > 0 && (
        <div className="space-y-6">
          {posts.map((post) => (
            <Card key={post.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                {/* Post Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(post.platform)}
                    <div>
                      <p className="font-medium text-sm">{post.author}</p>
                      <p className="text-xs text-muted-foreground">{formatTimestamp(post.timestamp)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                      Approved
                    </Badge>
                    {post.post_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Media */}
                {post.media_url && (
                  <div className="mb-4">
                    <img 
                      src={post.media_url} 
                      alt="Post media" 
                      className="w-full max-h-96 object-cover rounded-lg"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Content */}
                {post.content && (
                  <p className="text-sm mb-4 leading-relaxed">{post.content}</p>
                )}

                {/* Engagement */}
                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Heart className="h-4 w-4" />
                    <span>{formatEngagement(post.engagement.likes)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4" />
                    <span>{formatEngagement(post.engagement.comments)}</span>
                  </div>
                  {post.engagement.shares > 0 && (
                    <div className="flex items-center gap-1">
                      <Share className="h-4 w-4" />
                      <span>{formatEngagement(post.engagement.shares)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CuratedFeedPage;